2
<script setup lang="ts">
import { watch, onBeforeMount, defineAsyncComponent } from "vue"
import { useUserStore } from "@/stores/UserStore.ts"
const UserStore = useUserStore()

import { useRoute } from "vue-router"
const route = useRoute()

// Routes where a PM gets a context-specific nav (developer- or translator-flavored)
// instead of NavigationAsync. The "Exit" button inside those navs sets
// UserStore.showPMNavOverride to swap back without leaving the page. Reset the
// override when the PM leaves all such routes.
const developerRoutes = ["developer-view", "request-view"]
// All developer-flavored routes (PM-impersonating and the developer's own).
// Used to hide the top-right bug icon there — bug reporting lives in the
// developer Help menu instead.
const allDeveloperRoutes = [...developerRoutes, "developer-home", "request"]
const translatorRoutes = ["jobs", "translatorCalendar", "myinvoices"]
const contextNavRoutes = [...developerRoutes, ...translatorRoutes]
watch(
  () => route.name,
  (name) => {
    if (!contextNavRoutes.includes(name as string)) {
      UserStore.showPMNavOverride = false
    }
  }
)

const NotificationsAsync = defineAsyncComponent(() => import("@/components/TopNav/Notifications.vue"))
const ChangeThemeAsync = defineAsyncComponent(() => import("@/components/TopNav/ChangeTheme.vue"))
const BabbleMenuAsync = defineAsyncComponent(() => import("@/components/TopNav/BabbleMenu.vue"))
const NavigationAsync = defineAsyncComponent(() => import("@/components/TopNav/Navigation.vue"))
const NewsAsync = defineAsyncComponent(() => import("@/components/TopNav/News.vue"))
const NavigationTranslator = defineAsyncComponent(() => import("@/components/TopNav/NavigationTranslator.vue"))
const NavigationDeveloper = defineAsyncComponent(() => import("@/components/TopNav/NavigationDeveloper.vue"))
const DBLoadingIndicator = defineAsyncComponent(() => import("@/components/TopNav/DBLoadingIndicator.vue"))
const BugReportAsync = defineAsyncComponent(() => import("@/components/TopNav/BugReport.vue"))

const isProd = import.meta.env.PROD

onBeforeMount(async () => {
  await UserStore.fill()
})
</script>

<template>
  <nav class="navbar fixed-top navbar-expand-lg bg-dark text-light border-bottom border-body" style="z-index: 2000">
    <div class="container-fluid gap-1 align-items-center">
      <div class="d-flex align-items-center">
        <BabbleMenuAsync v-if="UserStore.pm" />
        <img
          v-else
          srcset="
            https://www.ibabbleon.com/pix/logo-tower-tr.png    2x,
            https://www.ibabbleon.com/pix/logo-tower-tr-2x.png 1.5x,
            https://www.ibabbleon.com/pix/logo-tower-tr-1x.png 1x
          "
          src="https://www.ibabbleon.com/pix/logo-tower-tr-mini.png"
          style="max-height: 2rem; margin-top: 0.5rem"
          alt="Tower of Babble-on App Localization Service"
        />
        <Button
          v-if="UserStore.pm"
          text
          as="router-link"
          class="text-decoration-none d-none d-md-inline-flex"
          :to="`/${UserStore.pm ? '' : 'jobs/'}`"
          size="large"
          severity="primary"
        >
          Babble-on
        </Button>
        <DBLoadingIndicator />
      </div>

      <div class="d-flex gap-1" v-if="UserStore.pm">
        <NavigationDeveloper v-if="developerRoutes.includes(route.name as string) && !UserStore.showPMNavOverride" :user="UserStore.user" />
        <NavigationTranslator v-else-if="translatorRoutes.includes(route.name as string) && !UserStore.showPMNavOverride" :user="UserStore.user" />
        <NavigationAsync v-else />
      </div>
      <div class="d-flex gap-1" v-else-if="UserStore.translator">
        <NavigationTranslator :user="UserStore.user" />
      </div>
      <div class="d-flex gap-1" v-else-if="UserStore.developer">
        <NavigationDeveloper :user="UserStore.user" />
      </div>
      <div v-else class="d-none d-md-block">
        <div class="h3">{{ "test".includes("babblesf") ? "Babble SF" : "Babble-on" }}</div>
      </div>

      <div class="d-inline-flex gap-1 align-items-center flex-shrink-0">
        <BugReportAsync v-if="!allDeveloperRoutes.includes(route.name as string) || UserStore.showPMNavOverride" />
        <ChangeThemeAsync />

        <NotificationsAsync v-if="UserStore.pm && !(contextNavRoutes.includes(route.name as string) && !UserStore.showPMNavOverride)" />
        <NewsAsync v-if="UserStore.user?.username != undefined && UserStore.translator" :key="UserStore.user.user_id" />
        <UserMenu v-if="UserStore.user?.username != undefined" :key="UserStore.user.user_id" />
        <Button text v-else as="router-link" :to="`/login${route.path == '/login' ? '' : `?to=${route.path}`}`"
          ><font-awesome-icon icon="fal fa-user" :fade="true" size="lg"
        /></Button>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.logo {
  display: block;
  margin: 0 auto 2rem;
}

@media (min-width: 1024px) {
  .logo {
    margin: 0 2rem 0 0;
  }
}
</style>
